import { forwardRef, useImperativeHandle, useMemo, useRef, useState, useEffect, useCallback } from "react";
import { Plus, Loader2, Users } from "lucide-react";
import { roomPixel, worldSize, ROOM_W, ROOM_H } from "@/lib/lifeos";
import { avatarForId } from "@/lib/avatars";

const MIN_ZOOM = 0.35;
const MAX_ZOOM = 1.7;
const clamp = (v, a, b) => Math.max(a, Math.min(b, v));

function CityBuilding({ dept, deptAgents, activeCount, onEnter, onHire, onAgentClick }) {
  const p = roomPixel(dept);
  const color = dept.color || "#00f0ff";
  const glow = dept.glow || `${color}33`;
  const h = 150 + ((dept.code?.charCodeAt(0) || 65) % 4) * 26;
  return (
    <div className="absolute" style={{ left: p.x, top: p.y, width: ROOM_W, height: ROOM_H }}>
      {/* ground pad */}
      <div className="absolute rounded-2xl" style={{ inset: 0, background: "radial-gradient(ellipse at 50% 60%, rgba(255,255,255,0.04), transparent 70%)", border: `1px solid ${color}22`, boxShadow: `0 0 60px ${glow}` }} />

      {/* building block (upright 2.5D) */}
      <div
        data-testid="department-room"
        onClick={() => onEnter(dept)}
        className="absolute left-1/2 -translate-x-1/2 rounded-xl cursor-pointer group transition-transform duration-300 hover:-translate-y-1"
        style={{
          top: 30, width: ROOM_W - 70, height: h,
          background: `linear-gradient(180deg, ${color}22 0%, #0a0e15 60%)`,
          border: `1.5px solid ${color}`, borderTop: `3px solid ${color}`,
          boxShadow: `0 26px 50px -12px ${glow}, 0 14px 0 -4px ${color}22, inset 0 0 50px ${color}14`,
          backgroundImage: `linear-gradient(180deg, ${color}18, transparent 55%), repeating-linear-gradient(90deg, transparent 0 26px, ${color}1f 26px 28px), repeating-linear-gradient(0deg, transparent 0 22px, ${color}14 22px 24px)`,
        }}
      >
        <div className="flex items-center gap-2 px-3 pt-2.5">
          <span className="font-mono text-[10px] font-bold px-1.5 py-0.5 rounded" style={{ background: `${color}22`, color, border: `1px solid ${color}55` }}>{dept.code}</span>
          {activeCount > 0 && <span className="ml-auto flex items-center gap-1 text-[10px] font-mono" style={{ color }}><span className="w-1.5 h-1.5 rounded-full anim-flicker" style={{ background: color, boxShadow: `0 0 6px ${color}` }} />{activeCount} live</span>}
        </div>
        {/* avatar stack */}
        <div className="absolute bottom-2.5 left-0 right-0 flex justify-center -space-x-2">
          {deptAgents.slice(0, 5).map((a) => (
            <button
              key={a.id}
              data-testid="agent-node"
              onClick={(e) => { e.stopPropagation(); onAgentClick(a); }}
              onPointerDown={(e) => e.stopPropagation()}
              className="rounded-full overflow-hidden hover:scale-110 hover:z-10 transition-transform"
              style={{ width: 30, height: 30, border: `2px solid ${color}`, background: "#0b0e14", boxShadow: `0 0 8px ${color}66` }}
              title={`${a.name} · ${a.role}`}
            >
              <img src={avatarForId(a.id)} alt={a.name} className="w-full h-full object-cover object-top" draggable="false" />
            </button>
          ))}
          {deptAgents.length === 0 && <span className="text-[10px] font-mono text-zinc-600">empty</span>}
        </div>
      </div>

      {/* floating label */}
      <div className="absolute left-1/2 -translate-x-1/2 -top-1 text-center pointer-events-none">
        <div className="font-display font-black uppercase tracking-wide text-[13px] text-zinc-50" style={{ textShadow: `0 0 14px ${color}, 0 2px 4px #000` }}>{dept.name}</div>
        <div className="font-mono text-[9px] text-zinc-400">{deptAgents.length} agents</div>
      </div>

      {/* hire */}
      <button
        data-testid="room-hire-button"
        onClick={(e) => { e.stopPropagation(); onHire(dept); }}
        onPointerDown={(e) => e.stopPropagation()}
        className="absolute top-8 right-2 w-6 h-6 grid place-items-center rounded-md bg-black/40 border border-white/10 text-zinc-400 opacity-0 group-hover:opacity-100 hover:text-cyan-300 transition-all z-10"
        title="Assign agent"
      >
        <Plus size={13} />
      </button>
    </div>
  );
}

const IsometricWorld = forwardRef(function IsometricWorld({ departments, agents, live, deptById, loading, onAgentClick, onRoomHire }, ref) {
  const viewportRef = useRef(null);
  const [cam, setCam] = useState({ x: 120, y: 60, zoom: 0.7 });
  const [dragging, setDragging] = useState(false);
  const pointers = useRef(new Map());
  const dragStart = useRef(null);
  const pinchStart = useRef(null);
  const didInit = useRef(false);

  const size = useMemo(() => worldSize(departments), [departments]);

  const centerOn = useCallback((cx, cy, zoom) => {
    const vp = viewportRef.current;
    if (!vp) return;
    setCam({ zoom, x: vp.clientWidth / 2 - cx * zoom, y: vp.clientHeight / 2 - cy * zoom });
  }, []);
  const focusRoom = useCallback((dept) => { const p = roomPixel(dept); centerOn(p.x + ROOM_W / 2, p.y + ROOM_H / 2, 1.0); }, [centerOn]);
  const recenter = useCallback(() => {
    const vp = viewportRef.current;
    if (!vp || !size.width) return;
    const zoom = clamp(Math.min(vp.clientWidth / size.width, vp.clientHeight / size.height) * 0.92, MIN_ZOOM, MAX_ZOOM);
    centerOn(size.width / 2, size.height / 2, zoom);
  }, [centerOn, size]);

  useImperativeHandle(ref, () => ({
    focusRoom, recenter,
    zoomIn: () => setCam((c) => ({ ...c, zoom: clamp(c.zoom * 1.2, MIN_ZOOM, MAX_ZOOM) })),
    zoomOut: () => setCam((c) => ({ ...c, zoom: clamp(c.zoom / 1.2, MIN_ZOOM, MAX_ZOOM) })),
  }), [focusRoom, recenter]);

  useEffect(() => { if (!didInit.current && departments.length && viewportRef.current) { didInit.current = true; setTimeout(recenter, 60); } }, [departments, recenter]);

  const onPointerDown = (e) => {
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (pointers.current.size === 1) { setDragging(true); dragStart.current = { px: e.clientX, py: e.clientY, cam: { ...cam } }; }
    else if (pointers.current.size === 2) { const pts = [...pointers.current.values()]; pinchStart.current = { dist: Math.hypot(pts[0].x - pts[1].x, pts[0].y - pts[1].y), zoom: cam.zoom }; }
  };
  const onPointerMove = (e) => {
    if (!pointers.current.has(e.pointerId)) return;
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (pointers.current.size >= 2 && pinchStart.current) {
      const pts = [...pointers.current.values()];
      const ratio = Math.hypot(pts[0].x - pts[1].x, pts[0].y - pts[1].y) / pinchStart.current.dist;
      setCam((c) => ({ ...c, zoom: clamp(pinchStart.current.zoom * ratio, MIN_ZOOM, MAX_ZOOM) }));
      return;
    }
    if (dragging && dragStart.current) {
      setCam((c) => ({ ...c, x: dragStart.current.cam.x + (e.clientX - dragStart.current.px), y: dragStart.current.cam.y + (e.clientY - dragStart.current.py) }));
    }
  };
  const endPointer = (e) => { pointers.current.delete(e.pointerId); if (pointers.current.size < 2) pinchStart.current = null; if (pointers.current.size === 0) setDragging(false); };
  const onWheel = (e) => {
    e.preventDefault();
    const rect = viewportRef.current.getBoundingClientRect();
    const mx = e.clientX - rect.left, my = e.clientY - rect.top;
    setCam((c) => { const nz = clamp(c.zoom * (e.deltaY < 0 ? 1.12 : 1 / 1.12), MIN_ZOOM, MAX_ZOOM); const wx = (mx - c.x) / c.zoom, wy = (my - c.y) / c.zoom; return { zoom: nz, x: mx - wx * nz, y: my - wy * nz }; });
  };

  return (
    <div ref={viewportRef} data-testid="simulation-canvas-container" className="w-full h-full overflow-hidden hq-grid relative" style={{ cursor: dragging ? "grabbing" : "grab", touchAction: "none" }}
      onPointerDown={onPointerDown} onPointerMove={onPointerMove} onPointerUp={endPointer} onPointerLeave={endPointer} onPointerCancel={endPointer} onWheel={onWheel}>
      {loading && (
        <div className="absolute inset-0 grid place-items-center z-30 text-cyan-300"><div className="flex flex-col items-center gap-3"><Loader2 className="animate-spin" size={28} /><span className="font-mono text-xs uppercase tracking-[0.3em]">Booting headquarters...</span></div></div>
      )}
      <div className="absolute top-0 left-0 origin-top-left" style={{ width: size.width, height: size.height, transform: `translate3d(${cam.x}px, ${cam.y}px, 0) scale(${cam.zoom})`, transition: dragging ? "none" : "transform 0.55s cubic-bezier(.22,.61,.36,1)" }}>
        {/* glowing roads */}
        <svg className="absolute inset-0 pointer-events-none" width={size.width} height={size.height} style={{ overflow: "visible" }}>
          {departments.map((d) => {
            const p = roomPixel(d);
            return departments.filter((o) => (o.gx === d.gx + 1 && o.gy === d.gy) || (o.gy === d.gy + 1 && o.gx === d.gx)).map((o) => {
              const q = roomPixel(o);
              return <line key={d.id + o.id} x1={p.x + ROOM_W / 2} y1={p.y + ROOM_H / 2} x2={q.x + ROOM_W / 2} y2={q.y + ROOM_H / 2} stroke="#00f0ff" strokeWidth="2" strokeOpacity="0.18" className="anim-dash" />;
            });
          })}
        </svg>
        {departments.map((d) => {
          const deptAgents = agents.filter((a) => a.department_id === d.id);
          const activeCount = deptAgents.filter((a) => { const l = live[a.id]; return l && (l.phase === "working" || l.phase === "walking"); }).length;
          return <CityBuilding key={d.id} dept={d} deptAgents={deptAgents} activeCount={activeCount} onEnter={focusRoom} onHire={onRoomHire} onAgentClick={onAgentClick} />;
        })}
      </div>
    </div>
  );
});

export default IsometricWorld;
